import React, { useState, useContext, useEffect, useCallback } from 'react';
import { Helmet } from 'react-helmet-async';
import { Link } from 'react-router-dom';
import { trackEvent, trackSearchEvent, trackFilterChange, trackPredefinedFilter, EVENTS } from '../utils/analytics';
import { AuthContext } from '../context/AuthContext';
import StockCard from '../components/StockCard';
import SortingControl from '../components/SortingControl';
import config from '../config/config';
import '../styles/Search.css';
import '../styles/StockCard.css';

const Search = () => {
  const { token } = useContext(AuthContext);
  const [searchResults, setSearchResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchInitiated, setSearchInitiated] = useState(false);
  const [formData, setFormData] = useState({
    name: '',
    sector: 'All',
    country: 'All',
    // Valuation metrics
    market_cap: { value: 0, label: 'Marktkapitalisierung', unit: 'B', max: 1000, isMax: false },
    trailing_p_e: { value: 0, label: 'KGV', unit: '', max: 100, isMax: true },
    forward_p_e: { value: 0, label: 'KGV erwartet', unit: '', max: 100, isMax: true },
    price_to_book: { value: 0, label: 'KBV', unit: '', max: 20, isMax: true },
    enterprise_to_ebitda: { value: 0, label: 'Wert/EBITDA', unit: '', max: 50, isMax: true },
    // Income metrics
    dividend: { value: 0, label: 'Dividende pro Aktie', unit: '', max: 10, isMax: false },
    dividend_yield: { value: 0, label: 'Dividendenrendite', unit: '%', max: 20, isMax: false },
    profit_margin: { value: 0, label: 'Gewinnmarge', unit: '%', max: 100, isMax: false },
    operating_margin: { value: 0, label: 'Operative Marge', unit: '%', max: 100, isMax: false },
    // Growth metrics
    next_year_growth: { value: 0, label: 'Erw. Wachstum (Jahr)', unit: '%', max: 100, isMax: false },
    revenue_growth: { value: 0, label: 'Umsatzwachstum', unit: '%', max: 100, isMax: false },
    earnings_growth: { value: 0, label: 'Gewinnwachstum', unit: '%', max: 100, isMax: false }
  });

  const [preferredCurrency, setPreferredCurrency] = useState('USD');
  const [sortBy, setSortBy] = useState('market_cap');
  const [sortOrder, setSortOrder] = useState('desc');

  const [collapsedSections, setCollapsedSections] = useState({
    valuation: true,
    income: true,
    growth: true
  });
  
  const [activeFilter, setActiveFilter] = useState(null);
  const [searchStartTime, setSearchStartTime] = useState(0);
  const [lastSearchParams, setLastSearchParams] = useState({});

  const predefinedFilters = [
    { 
      id: 'high-dividend', 
      label: 'Dividendenstars', 
      filters: {
        dividend_yield: 3.5,
        market_cap: 1
      }
    },
    { 
      id: 'growth-stocks', 
      label: 'Wachstumsaktien', 
      filters: {
        revenue_growth: 15,
        earnings_growth: 10,
        next_year_growth: 10
      }
    },
    { 
      id: 'value-stocks', 
      label: 'Value-Aktien', 
      filters: {
        trailing_p_e: 15,
        price_to_book: 2.5,
        dividend_yield: 2
      }
    },
    { 
      id: 'high-profit-margin', 
      label: 'Hohe Margen', 
      filters: {
        profit_margin: 25,
        operating_margin: 30,
        return_on_equity: 20
      }
    },
    { 
      id: 'undervalued-growth', 
      label: 'Unterbewertete Wachstumswerte', 
      filters: {
        forward_p_e: 20,
        revenue_growth: 20,
        price_to_book: 3
      }
    }
  ];

  // Modified toggleSection with analytics
  const toggleSection = (section) => {
    trackEvent(EVENTS.FILTER_SECTION_TOGGLE, {
      section_name: section,
      new_state: !collapsedSections[section] ? 'expanded' : 'collapsed'
    });
    
    setCollapsedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const renderSlider = (name, config) => (
    <div className="slider-group">
      <div className="slider-header">
        <span className="slider-label">
          {config.label}
          {config.isMax ? ' (Maximum)' : ' (Minimum)'}
        </span>
        <span className="slider-value">
          {config.value}{config.unit}
        </span>
      </div>
      <input
        type="range"
        className="modern-range"
        name={name}
        value={config.value}
        min="0"
        max={config.max || 100}
        step={config.unit === '%' ? '0.1' : '1'}
        onChange={handleInputChange}
      />
      <div className="slider-limits">
        <span>0{config.unit}</span>
        <span>{config.max || 100}{config.unit}</span>
      </div>
    </div>
  );

  // Modified addToWatchlist with analytics
  const addToWatchlist = async (stockId) => {
    try {
      // Find the stock in results
      const stock = searchResults.find(s => s.id === stockId);
      
      // Track the action before making the API call
      trackEvent(EVENTS.ADD_TO_WATCHLIST, {
        stock_id: stockId,
        stock_name: stock?.name,
        from_search: true,
        search_term: lastSearchParams.searchTerm || "",
        price: stock?.current_price,
        currency: stock?.display_currency
      });
      
      const response = await fetch(config.apiUrl +'/api/watchlist/' + stockId, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        }
      });
      
      if (response.ok) {
        alert('Erfolgreich zur Watchlist hinzugefügt');
        
        // Track successful completion
        trackEvent(EVENTS.ADD_TO_WATCHLIST_SUCCESS, {
          stock_id: stockId
        });
      }
    } catch (error) {
      console.error('Failed to add to watchlist:', error);
      alert('Fehler beim Hinzufügen zur Watchlist');
      
      // Track error
      trackEvent(EVENTS.API_ERROR, {
        error_type: 'watchlist_add_error',
        stock_id: stockId,
        error_message: error.message
      });
    }
  };

  const applyPredefinedFilter = (filter) => {
    // Track the filter action before applying changes
    const isRemoving = activeFilter && activeFilter.id === filter.id;
    
    trackPredefinedFilter({
      filterId: filter.id,
      filterName: filter.label,
      filterCount: Object.keys(filter.filters).length,
      replacesExisting: activeFilter !== null && !isRemoving
    });
    
    // If clicking the active filter, deactivate it
    if (isRemoving) {
      setActiveFilter(null);
      // Reset all form values affected by this filter
      const newFormData = {...formData};
      Object.keys(filter.filters).forEach(key => {
        // Track each filter being reset
        trackFilterChange({
          filter_name: key,
          filter_value: 0,
          filter_previous: newFormData[key].value,
          filter_category: newFormData[key]?.label || key,
          filter_unit: newFormData[key]?.unit || '',
          filter_source: "preset_removal"
        });
        newFormData[key].value = 0;
      });
      setFormData(newFormData);
      return;
    }

    // Apply the filter
    setActiveFilter(filter);
    const newFormData = {...formData};
    
    // First reset all values
    Object.keys(newFormData).forEach(key => {
      if (typeof newFormData[key] === 'object' && newFormData[key].hasOwnProperty('value')) {
        const oldValue = newFormData[key].value;
        newFormData[key].value = 0;
        
        // Only track changes for values that are actually changing
        if (oldValue > 0) {
          trackFilterChange({
            filter_name: key,
            filter_value: filter.filters[key],
            filter_previous: oldValue,
            filter_category: newFormData[key]?.label || key,
            filter_unit: newFormData[key]?.unit || '',
            filter_source: "preset_application"
          });
        }
      }
    });
    
    // Then apply the new filter values
    Object.keys(filter.filters).forEach(key => {
      if (newFormData[key]) {
        const oldValue = newFormData[key].value;
        newFormData[key].value = filter.filters[key];
        
        // Track the newly applied filter value
        trackFilterChange({
          filter_name: key,
          filter_value: filter.filters[key],
          filter_previous: oldValue,
          filter_category: newFormData[key]?.label || key,
          filter_unit: newFormData[key]?.unit || '',
          filter_source: "preset_application"
        });
      }
    });
    
    setFormData(newFormData);
  };

 // Modified handleSubmit with enhanced analytics
 const handleSubmit = useCallback(async (e) => {
  if (e) e.preventDefault();
  setLoading(true);
  setError(null);
  setSearchInitiated(true);
  
  // Record search start time for performance tracking
  const startTime = Date.now();
  setSearchStartTime(startTime);
  
  // Prepare filter analytics data
  const activeFilters = {};
  Object.entries(formData).forEach(([key, config]) => {
    if (typeof config === 'object' && config.value > 0) {
      activeFilters[key] = config.value;
    }
  });
  
  // Save search parameters for results tracking
  const searchParams = {
    searchTerm: formData.name,
    sector: formData.sector,
    currency: preferredCurrency,
    activeFilters: formData,
    startTime
  };
  setLastSearchParams(searchParams);
  
  // Track search initiation
  trackEvent(EVENTS.SEARCH_INITIATED, {
    search_term: formData.name || "[empty]",
    sector: formData.sector,
    currency: preferredCurrency,
    filter_count: Object.keys(activeFilters).length,
    has_filters: Object.keys(activeFilters).length > 0 ? "yes" : "no",
    using_preset: activeFilter ? "yes" : "no",
    preset_name: activeFilter ? activeFilter.label : null
  });

  try {
    // Transform the form data to match backend expectations
    const apiSearchParams = {
      name: formData.name,
      country: formData.country,
      sector: formData.sector,
      currency: preferredCurrency
    };
    
    // Add all active numeric filters
    Object.entries(formData).forEach(([key, config]) => {
      if (typeof config === 'object' && config.value > 0) {
        apiSearchParams[key] = config.value;
      }
    });

    // Make initial search request to get basic stock info
    const response = await fetch(config.apiUrl + '/api/stocks/search', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token && { 'Authorization': `Bearer ${token}` })
      },
      body: JSON.stringify(apiSearchParams)
    });
    
    if (!response.ok) throw new Error('Netzwerkfehler bei der Suche');
    
    const result = await response.json();
    if (result.error) throw new Error(result.error);
    
    // Store basic search results
    const basicResults = result.data || [];
    
    // Calculate query time for analytics
    const queryTimeMs = Date.now() - startTime;
    
    // Track search results with comprehensive data
    trackSearchEvent({
      searchTerm: formData.name,
      sector: formData.sector,
      activeFilters: formData,
      currency: preferredCurrency,
      resultsCount: basicResults.length,
      queryTimeMs,
      sortBy,
      sortOrder
    });
    
    // Show loading indicator for detailed data
    setLoading(true);
    
    // Create a function to fetch details for a single stock
    const fetchStockDetails = async (stockId) => {
      try {
        const headers = {};
        if (token) {
          // Use token directly for authorization if getAuthHeaders isn't available
          headers.Authorization = `Bearer ${token}`;
        }
        
        const detailResponse = await fetch(
          `${config.apiUrl}/api/stocks/${stockId}?currency=${preferredCurrency}`,
          { headers }
        );
        
        if (!detailResponse.ok) {
          console.error(`Failed to fetch details for stock ${stockId}`);
          return null;
        }
        
        const detailData = await detailResponse.json();
        return detailData.success ? detailData.data : null;
      } catch (err) {
        console.error(`Error fetching stock details for ${stockId}:`, err);
        return null;
      }
    };
    
    // Batch fetch stock details to avoid too many parallel requests
    const batchSize = 5;
    const detailedResults = [];
    
    for (let i = 0; i < basicResults.length; i += batchSize) {
      const batch = basicResults.slice(i, i + batchSize);
      
      const batchResults = await Promise.all(
        batch.map(stock => fetchStockDetails(stock.id))
      );
      
      detailedResults.push(...batchResults.filter(result => result !== null));
      
      // Optional: Update search results incrementally as each batch completes
      setSearchResults([...detailedResults]);
    }
    
    // Final update with all detailed results
    setSearchResults(detailedResults);
    
  } catch (error) {
    console.error('Suchfehler:', error);
    setError(error.message);
    
    // Track search error
    trackEvent(EVENTS.API_ERROR, {
      error_type: 'search_error',
      error_message: error.message,
      search_term: formData.name,
      has_filters: Object.values(formData).some(v => typeof v === 'object' && v.value > 0) ? "yes" : "no"
    });
  } finally {
    setLoading(false);
  }
}, [formData, preferredCurrency, token, activeFilter, sortBy, sortOrder]);


  useEffect(() => {
    const fetchPreferredCurrency = async () => {
      if (token) {
        try {
          const response = await fetch(config.apiUrl + '/api/portfolio/preferred-currency', {
            headers: {
              'Authorization': `Bearer ${token}`
            }
          });
          const data = await response.json();
          setPreferredCurrency(data.currency);
        } catch (error) {
          console.error('Error fetching preferred currency:', error);
          setError('Fehler beim Laden der bevorzugten Währung');
        }
      }
    };
    
    fetchPreferredCurrency();
  }, [token]);


  // Track stock clicks
  const handleStockClick = (stock, index) => {
    trackEvent(EVENTS.SEARCH_RESULT_CLICK, {
      stock_id: stock.id,
      stock_name: stock.name,
      position: index + 1,
      result_count: searchResults.length
    });
  };

  // Modified handleInputChange with analytics
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    
    // For form fields
    if (name === 'name' || name === 'sector' || name === 'country') {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
      
      // Track significant text search changes (not every keystroke)
      if (name === 'name' && value.length > 2 && value.length % 5 === 0) {
        trackEvent(EVENTS.SEARCH_INPUT_CHANGE, {
          input_field: 'name',
          input_length: value.length
        });
      }
      
      if ((name === 'sector' || name === 'country') && value !== formData[name]) {
        trackEvent(EVENTS.FILTER_CHANGE, {
          filter_name: name,
          old_value: formData[name],
          new_value: value
        });
      }
    } 
    // For sliders and numeric inputs
    else {
      const oldValue = formData[name].value;
      const newValue = parseFloat(value);
      
      setFormData(prev => ({
        ...prev,
        [name]: {
          ...prev[name],
          value: newValue
        }
      }));
      
      // Only track meaningful changes in filter values
      if (Math.abs(newValue - oldValue) > 0.1) {
        trackFilterChange({
          filter_name: name,
          filter_value: newValue,
          filter_previous: oldValue,
          filter_category: formData[name]?.label || name,
          filter_unit: formData[name]?.unit || '',
          filter_source: "manual_adjustment"
        });
      }
    }
    
    // Reset active filter when manually changing values
    if (activeFilter) {
      trackEvent(EVENTS.FILTER_PRESET_CANCELED, {
        filter_id: activeFilter.id,
        filter_name: activeFilter.label,
        changed_field: name
      });
      setActiveFilter(null);
    }
  };

  return (
    <>
      <Helmet>
        <title>Aktiensuche | DynaStoxx</title>
        <meta name="description" content="Suche nach den richtigen Aktien für dein Portfolio" />
      </Helmet>
      <div className="search-container">
        <main>
          {error && (
            <div className="error-message">
              <i className="fas fa-exclamation-circle"></i>
              {error}
            </div>
          )}
          
          {/* Introduction Card */}
          <div className="search-card intro-card">
            <h2>Aktiensuche</h2>
            <p className="text-muted">
              Finde die passende Aktien anhand Deiner Investitionskriterien. Filtere nach Dividenden, 
              Bewertungskennzahlen, Wachstumsprognosen und mehr.
              <Link to="/search-explained" className="learn-more-link" style={{ 
                marginLeft: '8px', 
                color: 'var(--primary-color)',
                textDecoration: 'none',
                display: 'inline-flex',
                alignItems: 'center' 
              }}>
                <i className="fas fa-info-circle" style={{ marginRight: '4px' }}></i>
                Mehr erfahren
              </Link>
            </p>
            <div className="input-group">
              <span className="input-group-text">
                <i className="fas fa-money-bill"></i>
              </span>
              <select
                className="form-control"
                value={preferredCurrency}
                onChange={(e) => setPreferredCurrency(e.target.value)}
              >
                <option value="USD">USD ($)</option>
                <option value="EUR">EUR (€)</option>
                <option value="GBP">GBP (£)</option>
                <option value="JPY">JPY (¥)</option>
              </select>
            </div>
          </div>

          {/* Configuration Form */}
          <div className="search-card">
            {loading && (
              <div className="loading-overlay">
                <div className="loading-spinner"></div>
                <span>Suche läuft...</span>
              </div>
            )}
            <form onSubmit={handleSubmit}>
              {/* Search and Sector */}
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-search"></i>
                </span>
                <input
                  type="text"
                  name="name"
                  className="form-control"
                  placeholder="Aktien suchen..."
                  value={formData.name}
                  onChange={handleInputChange}
                />
              </div>

              {/* Country Selection Dropdown */}
              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-globe"></i>
                </span>
                <select
                  name="country"
                  className="form-control"
                  value={formData.country || 'All'}
                  onChange={handleInputChange}
                >
                  <option value="All">Alle Länder</option>
                  <option value="United States">USA</option>
                  <option value="Germany">Deutschland</option>
                  <option value="United Kingdom">Großbritannien</option>
                  <option value="Switzerland">Schweiz</option>
                  <option value="France">Frankreich</option>
                  <option value="Netherlands">Niederlande</option>
                  <option value="Sweden">Schweden</option>
                  <option value="Canada">Kanada</option>
                  <option value="China">China</option>
                  <option value="Japan">Japan</option>
                  <option value="Other">Andere</option>
                </select>
              </div>

              <div className="input-group">
                <span className="input-group-text">
                  <i className="fas fa-industry"></i>
                </span>
                <select
                  name="sector"
                  className="form-control"
                  value={formData.sector}
                  onChange={handleInputChange}
                >
                  <option value="All">Alle Sektoren</option>
                  <option value="Technology">Technologie</option>
                  <option value="Finance">Finanzen</option>
                  <option value="Healthcare">Gesundheit</option>
                  <option value="Energy">Energie</option>
                  <option value="Consumer Cyclical">Zyklischer Konsum</option>
                  <option value="Consumer Defensive">Defensiver Konsum</option>
                  <option value="Industrials">Industrie</option>
                  <option value="Basic Materials">Grundstoffe</option>
                  <option value="Communication Services">Kommunikation</option>
                  <option value="Utilities">Versorger</option>
                  <option value="Real Estate">Immobilien</option>
                </select>
              </div> 

              {/* Predefined Filters */}
              <div className="predefined-filters">
                {predefinedFilters.map(filter => (
                  <button
                    key={filter.id}
                    type="button"
                    className={`filter-pill ${activeFilter && activeFilter.id === filter.id ? 'active' : ''}`}
                    onClick={() => applyPredefinedFilter(filter)}
                  >
                    {filter.label}
                  </button>
                ))}
              </div>

              {/* Sliders */}
              <div className="filters-container">
                <div className="filters-collapse">
                  {/* Valuation Metrics */}
                  <div className="filters-section">
                    <button 
                      type="button"
                      className="collapse-header" 
                      onClick={() => toggleSection('valuation')}
                    >
                      <h3>Bewertungskennzahlen</h3>
                      <i className={`fas fa-chevron-${collapsedSections.valuation ? 'down' : 'up'}`}></i>
                    </button>
                    
                    {!collapsedSections.valuation && (
                      <div className="collapse-content">
                        {renderSlider('market_cap', formData.market_cap)}
                        {renderSlider('trailing_p_e', formData.trailing_p_e)}
                        {renderSlider('forward_p_e', formData.forward_p_e)}
                        {renderSlider('price_to_book', formData.price_to_book)}
                        {renderSlider('enterprise_to_ebitda', formData.enterprise_to_ebitda)}
                      </div>
                    )}
                  </div>

                  {/* Income & Dividend Metrics */}
                  <div className="filters-section">
                    <button 
                      type="button"
                      className="collapse-header" 
                      onClick={() => toggleSection('income')}
                    >
                      <h3>Dividenden & Profitabilität</h3>
                      <i className={`fas fa-chevron-${collapsedSections.income ? 'down' : 'up'}`}></i>
                    </button>
                    
                    {!collapsedSections.income && (
                      <div className="collapse-content">
                        {renderSlider('dividend', formData.dividend)}
                        {renderSlider('dividend_yield', formData.dividend_yield)}
                        {renderSlider('profit_margin', formData.profit_margin)}
                        {renderSlider('operating_margin', formData.operating_margin)}
                      </div>
                    )}
                  </div>

                  {/* Growth Metrics */}
                  <div className="filters-section">
                    <button 
                      type="button"
                      className="collapse-header" 
                      onClick={() => toggleSection('growth')}
                    >
                      <h3>Wachstumskennzahlen</h3>
                      <i className={`fas fa-chevron-${collapsedSections.growth ? 'down' : 'up'}`}></i>
                    </button>
                    
                    {!collapsedSections.growth && (
                      <div className="collapse-content">
                        {renderSlider('next_year_growth', formData.next_year_growth)}
                        {renderSlider('revenue_growth', formData.revenue_growth)}
                        {renderSlider('earnings_growth', formData.earnings_growth)}
                      </div>
                    )}
                  </div>
                </div>

                {/* Submit Button */}
                <button type="submit" className="search-button">
                  {loading ? (
                    <span className="loading-spinner"></span>
                  ) : (
                    <>
                      <i className="fas fa-search"></i>
                      Aktien suchen
                    </>
                  )}
                </button>
              </div>
            </form>
          </div>

          {/* Results Section */}
          {searchInitiated && (
            <div className="search-card results-card">
              {searchResults.length > 0 ? (
                <>
                  <div className="results-header">
                    <h3>{searchResults.length} Ergebnisse gefunden</h3>
                    
                    <SortingControl
                        sortBy={sortBy}
                        onSortChange={(value) => setSortBy(value)}
                        sortOrder={sortOrder}
                        onSortOrderChange={(order) => setSortOrder(order)}
                        context="search"
                    />
                  </div>
                  
                  <div className="stock-grid">
                    {searchResults.sort((a, b) => {
                      let aValue, bValue;
                      
                      // Handle nested paths for special cases
                      const nestedPaths = {
                        'next_year_growth': 'growth_estimates.next_year',
                        'current_qtr_growth': 'growth_estimates.current_qtr',
                        'next_qtr_growth': 'growth_estimates.next_qtr',
                        'current_year_growth': 'growth_estimates.current_year'
                      };

                      const path = nestedPaths[sortBy] || sortBy;
                      
// Handle both direct and nested paths
const getValue = (obj, propertyPath) => {
  const value = propertyPath.includes('.') ? 
    propertyPath.split('.').reduce((curr, prop) => curr?.[prop], obj) : 
    obj[propertyPath];
  
  return value === null || value === undefined ? -Infinity : Number(value);
};

aValue = getValue(a, path);
bValue = getValue(b, path);

// Ensure numeric comparison
if (typeof aValue === 'number' && typeof bValue === 'number') {
  return sortOrder === 'asc' ? aValue - bValue : bValue - aValue;
}

// Fallback to string comparison
aValue = typeof aValue === 'string' ? aValue.toLowerCase() : String(aValue);
bValue = typeof bValue === 'string' ? bValue.toLowerCase() : String(bValue);

if (aValue < bValue) return sortOrder === 'asc' ? -1 : 1;
if (aValue > bValue) return sortOrder === 'asc' ? 1 : -1;
return 0;
}).map(stock => (
<StockCard
  key={stock.id}
  stock={stock}
  isPortfolio={false}
  onAction={addToWatchlist}
  showActions={!!token}
  sortBy={sortBy}
/>
))}
</div>
</>
) : (
<div className="results-header" style={{ textAlign: 'center', padding: '2rem 1rem' }}>
<h3 style={{ marginBottom: '1rem', color: 'var(--text-primary)' }}>
Keine Aktien gefunden
</h3>
<p style={{ color: 'var(--text-secondary)', lineHeight: '1.6' }}>
Leider wurden keine Aktien gefunden, die den Suchkriterien entsprechen. Bitte versuche es später erneut oder passe die Suchkriterien an.
</p>
</div>
)}
</div>
)}
</main>
</div>
</>
);
};

export default Search;